/**
 * Spotlight & UI Focus Highlighting Entity for ASAE Screencast Lessons.
 * Dims the screen background with an SVG cutout mask and highlights specific desktop UI elements
 * (Ribbon tabs, toolbar icons, formula bars, code lines) with an animated glowing border and optional tooltip.
 */
import { SVGObject, SVGObjectConfig } from './SVGObject';
import { Renderer } from '../core/Renderer';

export interface SpotlightProps {
  targetX: number;
  targetY: number;
  targetWidth: number;
  targetHeight: number;
  shape?: 'rect' | 'circle';
  borderRadius?: number;
  dimColor?: string;
  dimOpacity?: number;
  glowColor?: string;
  glowWidth?: number;
  stageWidth?: number;
  stageHeight?: number;
  title?: string;
  description?: string;
}

export class SpotlightObject extends SVGObject {
  public props: SpotlightProps;
  private static instanceCounter = 0;
  private maskId: string;

  constructor(config: SVGObjectConfig & { props?: SpotlightProps }) {
    super({ ...config, type: 'spotlight' });
    SpotlightObject.instanceCounter++;
    this.maskId = `spotlight-mask-${SpotlightObject.instanceCounter}`;
    
    // Lock container position at stage origin (0, 0)
    this.x = 0;
    this.y = 0;

    const targetX = config.props?.targetX !== undefined ? config.props.targetX : (config.x || 0);
    const targetY = config.props?.targetY !== undefined ? config.props.targetY : (config.y || 0);

    this.props = {
      targetX: targetX,
      targetY: targetY,
      targetWidth: 120,
      targetHeight: 50,
      shape: 'rect',
      borderRadius: 8,
      dimColor: '#000000',
      dimOpacity: 0.65,
      glowColor: '#38bdf8',
      glowWidth: 2.5,
      stageWidth: 1920,
      stageHeight: 1080,
      ...config.props
    };
  }

  public override applyTransform(): void {
    // Spotlight container must ALWAYS stay locked at canvas origin (0, 0)
    this.x = 0;
    this.y = 0;
    this.container.setAttribute('transform', 'translate(0, 0)');
  }

  public render(): void {
    this.container.innerHTML = '';
    const {
      targetX,
      targetY,
      targetWidth,
      targetHeight,
      shape,
      borderRadius,
      dimColor,
      dimOpacity,
      glowColor,
      glowWidth,
      title,
      description
    } = this.props;

    // Ensure SpotlightObject container always sits at canvas origin (0, 0)
    this.x = 0;
    this.y = 0;

    // Expand dim overlay rect to cover large viewports/zooms (-5000 to +5000)
    const overlayX = -5000;
    const overlayY = -5000;
    const overlayW = 10000;
    const overlayH = 10000;

    // 1. Defs Mask
    const defs = Renderer.createElement('defs');
    const mask = Renderer.createElement('mask');
    Renderer.setAttributes(mask, { id: this.maskId });

    // Full white rectangle covering entire stage area (-5000 to +5000)
    const maskBg = Renderer.createElement('rect');
    Renderer.setAttributes(maskBg, {
      x: overlayX,
      y: overlayY,
      width: overlayW,
      height: overlayH,
      fill: '#ffffff'
    });
    mask.appendChild(maskBg);

    // Black cutout shape (masked out / transparent)
    if (shape === 'circle') {
      const cutout = Renderer.createElement('circle');
      const r = Math.max(targetWidth, targetHeight) / 2;
      Renderer.setAttributes(cutout, {
        cx: targetX + targetWidth / 2,
        cy: targetY + targetHeight / 2,
        r: r,
        fill: '#000000'
      });
      mask.appendChild(cutout);
    } else {
      const cutout = Renderer.createElement('rect');
      Renderer.setAttributes(cutout, {
        x: targetX,
        y: targetY,
        width: targetWidth,
        height: targetHeight,
        rx: borderRadius || 8,
        ry: borderRadius || 8,
        fill: '#000000'
      });
      mask.appendChild(cutout);
    }
    defs.appendChild(mask);
    this.container.appendChild(defs);

    // 2. Dimmed Overlay Rect (covers entire centered stage seamlessly)
    const overlay = Renderer.createElement('rect');
    Renderer.setAttributes(overlay, {
      x: overlayX,
      y: overlayY,
      width: overlayW,
      height: overlayH,
      fill: dimColor || '#000000',
      opacity: dimOpacity ?? 0.65,
      mask: `url(#${this.maskId})`
    });
    this.container.appendChild(overlay);

    // 3. Glowing Border around target with vibrant pulsing highlight
    if (shape === 'circle') {
      const glow = Renderer.createElement('circle');
      const r = Math.max(targetWidth, targetHeight) / 2;
      Renderer.setAttributes(glow, {
        cx: targetX + targetWidth / 2,
        cy: targetY + targetHeight / 2,
        r: r,
        fill: 'none',
        stroke: glowColor || '#38bdf8',
        'stroke-width': glowWidth || 3.5,
        filter: 'drop-shadow(0px 0px 10px rgba(56, 189, 248, 0.9))'
      });
      this.container.appendChild(glow);
    } else {
      const glow = Renderer.createElement('rect');
      Renderer.setAttributes(glow, {
        x: targetX,
        y: targetY,
        width: targetWidth,
        height: targetHeight,
        rx: borderRadius || 8,
        ry: borderRadius || 8,
        fill: 'none',
        stroke: glowColor || '#38bdf8',
        'stroke-width': glowWidth || 3.5,
        filter: 'drop-shadow(0px 0px 10px rgba(56, 189, 248, 0.9))'
      });
      this.container.appendChild(glow);
    }

    // 4. Optional Tooltip Callout
    if (title || description) {
      const tooltipGroup = Renderer.createElement('g');
      const tooltipY = targetY + targetHeight + 16 > 460 ? targetY - 60 : targetY + targetHeight + 16;
      const tooltipX = Math.min(Math.max(-940, targetX), 680);

      const tooltipBg = Renderer.createElement('rect');
      Renderer.setAttributes(tooltipBg, {
        x: tooltipX,
        y: tooltipY,
        width: 260,
        height: description ? 54 : 36,
        rx: 8,
        ry: 8,
        fill: 'rgba(15, 23, 42, 0.95)',
        stroke: glowColor || '#38bdf8',
        'stroke-width': 1.5,
        filter: 'drop-shadow(0px 4px 12px rgba(0,0,0,0.5))'
      });
      tooltipGroup.appendChild(tooltipBg);

      if (title) {
        const titleText = Renderer.createElement('text');
        Renderer.setAttributes(titleText, {
          x: tooltipX + 12,
          y: tooltipY + 22,
          fill: '#f8fafc',
          'font-size': 13,
          'font-weight': '700',
          'font-family': 'Inter, system-ui, sans-serif'
        });
        titleText.textContent = title;
        tooltipGroup.appendChild(titleText);
      }

      if (description) {
        const descText = Renderer.createElement('text');
        Renderer.setAttributes(descText, {
          x: tooltipX + 12,
          y: tooltipY + 42,
          fill: '#94a3b8',
          'font-size': 11,
          'font-family': 'Inter, system-ui, sans-serif'
        });
        descText.textContent = description;
        tooltipGroup.appendChild(descText);
      }

      this.container.appendChild(tooltipGroup);
    }

    this.applyTransform();
  }

  /**
   * Updates target highlight region dynamically on timeline seek or animation.
   */
  public setTarget(x: number, y: number, w: number, h: number): void {
    this.props.targetX = x;
    this.props.targetY = y;
    this.props.targetWidth = w;
    this.props.targetHeight = h;
    this.render();
  }
}
